import { FC, ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { GetStaticPropsContext } from 'next'
import Head from 'next/head'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import { SwiperSlide } from 'swiper/react'
import useSWR from 'swr'
import clsx from 'clsx'
import { getEventsList, getTopEventsList } from '@service/events.service'
import { SEOData } from '@service/seo.service'
import retriveMessages, { LocaleType } from '@messages/message'
import { withSEO } from '@common/with-seo'
import { useMobileDetector } from '@hooks/use-mobile-detector'
import { useChinaDetector } from '@common/region-locale'
import { NextPageWithLayout } from '@common/common-types'
import PageLayout from '@components/page-layout/page-layout'
import PdSwiper from '@components/pd-swiper/pd-swiper'
import PdMedia from '@components/pd-media/pd-media'
import SlidingTabs from '@components/standard/pd-sliding-tabs'
import Pagination from '@components/standard/pagination'

import { EVENTS_CONDITION } from '@/types/enum'
import type { EVENTS } from '@types'
import { formatDate } from '@/common/date'

type Pagination = {
  totalPage: number
  currPage: number
}

type EventsMeta = {
  mediaUrl: string
  docId: string
  jumpUrl: string
  title: string
  date: string
  inPlace: string
  place: string
  status: EVENTS.EventsType
  statusValue: string
  isOutUrl: boolean
  outUrl: string
}

interface PdEventsCardBaseProps {
  data: EventsMeta
}

enum conditionEnum {
  'progressing' = 'ing',
  'will' = 'ready',
  'ended' = 'end',
}

const PdEventsCard: FC<PdEventsCardBaseProps> = ({ data }) => {
  const t = useTranslations()
  const condition = Object.entries(conditionEnum).find(([key, value]) => value === data?.status)
  const router = useRouter()

  return (
    <article
      className={clsx(
        'group bg-white cursor-pointer [&_a]:cursor-pointer',
        'w-full h-fit',
        'laptop:h-full laptop:flex laptop:flex-col'
      )}
      onClick={() => {
        if (data.isOutUrl) {
          window.open(data.outUrl, '_blank')
        } else {
          router.push(data.jumpUrl)
        }
      }}
    >
      <div className={clsx('flex-none', 'w-full h-208px', 'laptop:w-148 laptop:h-376px')}>
        <PdMedia src={data.mediaUrl} mask={false} stopAutoplay={true} showShadow={false} />
      </div>
      <aside className={clsx('flex-1 flex flex-col justify-between', 'h-fit p-3', 'laptop:min-h-66 laptop:p-6')}>
        <h3
          className={clsx('font-semibold line-clamp-2', 'text-black text-15', 'laptop:text-black/90 laptop:text-2xl')}
        >
          {data.title}
        </h3>

        <div className={clsx('mt-3', 'laptop:mt-6')}>
          <div className={clsx('flex flex-col gap-0.5', 'text-blue-600 font-semibold', 'text-sm', 'laptop:text-base')}>
            <p>{data.date}</p>
            <p className={clsx('line-clamp-1', 'text-black/60', 'laptop:text-inherit')}>{data.place}</p>
            <p className={clsx('line-clamp-1', 'text-black/60', 'laptop:text-inherit')}>{data.inPlace}</p>
          </div>
          <div className={clsx('flex justify-between items-center', 'mt-3', 'laptop:mt-34px')}>
            <div
              className={clsx(
                'rounded-full backdrop-blur-xl',
                'px-4 py-1 text-sm font-normal',
                'laptop:py-1.5',
                data.status === 'ing' ? 'bg-pdblue text-white' : 'text-black/60 bg-gray-100'
              )}
            >
              {data.statusValue ?? t(`condition.${condition?.[0]}`)}
            </div>
            <div
              className={clsx(
                'flex-none',
                'bg-[url("/icons/icon_indicator.svg")] bg-cover bg-no-repeat bg-center',
                'w-6 h-6',
                'opacity-100 laptop:opacity-0 group-hover:opacity-100 transition-opacity duration-300'
              )}
            />
          </div>
        </div>
      </aside>
    </article>
  )
}

const PdEventsBannerCard: FC<PdEventsCardBaseProps> = ({ data }) => {
  const t = useTranslations()
  const condition = Object.entries(conditionEnum).find(([key, value]) => value === data?.status)
  const router = useRouter()
  return (
    <article
      className={clsx(
        'group flex bg-blue-600 cursor-pointer select-none',
        'flex-col w-full h-fit',
        'laptop:flex-row laptop:max-w-300 laptop:h-full'
      )}
      onClick={() => {
        if (data.isOutUrl) {
          window.open(data.outUrl, '_blank')
        } else {
          router.push(data.jumpUrl)
        }
      }}
    >
      <div className={clsx('flex-none bg-white', 'w-full h-238px', 'laptop:w-200 laptop:h-full')}>
        <PdMedia src={data.mediaUrl} mask={false} stopAutoplay={true} linkHref={data.jumpUrl} />
      </div>
      <aside className={clsx('flex flex-col justify-between', 'h-278px p-4', 'laptop:flex-1 laptop:h-full laptop:p-6')}>
        <h2
          className={clsx(
            'text-sky-100',
            'text-lg font-semibold line-clamp-4',
            'laptop:text-2xl laptop:font-bold laptop:line-clamp-none'
          )}
        >
          {data.title}
        </h2>
        <div>
          <p
            className={clsx(
              'text-sky-100',
              'mb-3 text-lg font-semibold',
              'laptop:mb-4 laptop:text-2xl laptop:font-bold'
            )}
          >
            {data.date}
          </p>
          <p
            className={clsx(
              'text-orange-200',
              'text-15 font-normal mb-1',
              'laptop:text-xl laptop:font-medium laptop:mb-0'
            )}
          >
            {data.place}
          </p>
          <p className={clsx('text-orange-200', 'text-15 font-normal', 'laptop:text-xl laptop:font-medium')}>
            {data.inPlace}
          </p>
        </div>
        <div className={clsx('flex justify-between items-center')}>
          <div
            className={clsx(
              'rounded-full backdrop-blur-xl',
              'px-4 py-1 text-sm font-normal',
              'laptop:py-1.5',
              data.status === 'ing' ? 'text-blue-600 bg-white' : 'text-black/60 bg-gray-100'
            )}
          >
            {data.statusValue ?? t(`condition.${condition?.[0]}`)}
          </div>
          <div
            className={clsx(
              'bg-[url("/icons/icon_white_arrow.svg")] bg-cover bg-no-repeat',
              'w-6 h-6',
              'opacity-100 laptop:opacity-0 group-hover:opacity-100 transition-opacity duration-300'
            )}
          />
        </div>
      </aside>
    </article>
  )
}

const Events: NextPageWithLayout<{ initTopEventsList: EVENTS.EventList; seoData?: SEOData | null }> = ({
  initTopEventsList,
  seoData,
}) => {
  const t = useTranslations()
  const { locale, query } = useRouter()
  const inChina = useChinaDetector()
  const isMobileDevice = useMobileDetector()
  const [pageLimit, setPageLimit] = useState<number>(4) // 6 for pc, 4 for mobile
  const [eventsType, setEventsType] = useState<string>('') // '' => all types
  const eventsWrapperRef = useRef<HTMLDivElement>(null)
  const [eventsList, setEventsList] = useState<Array<EventsMeta>>([])
  const [pagination, setPagination] = useState<Pagination>({
    totalPage: 1,
    currPage: 1,
  })

  /* top events */
  const { data: _data, error: topError } = useSWR<EVENTS.EventList>(getTopEventsList.swrkey(locale || ''), () =>
    getTopEventsList(locale)
  )
  const topEventsList = _data || initTopEventsList

  // 标题
  const eventsTabsTitle = useMemo(() => {
    if (!topEventsList || !topEventsList.tabTitle) {
      return ''
    }

    return topEventsList.tabTitle
  }, [topEventsList, locale])

  // topTitle
  const topTitle = useMemo(() => {
    if (!topEventsList || !topEventsList.topTitle) {
      return ''
    }

    return topEventsList.topTitle
  }, [topEventsList, locale])

  // events主页 事件列表tabs
  const eventstabItems: Array<{
    key: string
    title: string
  }> = useMemo(() => {
    if (!topEventsList?.tabItem) {
      return []
    }

    return topEventsList.tabItem?.map((tab) => ({
      key: tab.key,
      title: tab.title,
    }))
  }, [locale, topEventsList])

  // events主页 事件banner
  const eventsBannerList: Array<EventsMeta> = useMemo(() => {
    if (!topEventsList) {
      return []
    }

    return topEventsList.items?.map((event) => ({
      docId: event.docId,
      title: event.title,
      mediaUrl: event.mediaUrl,
      inPlace: event.inPlace,
      place: event.place,
      jumpUrl: event?.docId ? `/events/${event?.docId}` : '#',
      date: `${formatDate(event.startTime, locale)} - ${formatDate(event.endTime, locale)}`,
      status: event.status,
      statusValue: event.statusValue,
      isOutUrl: event.isOutUrl,
      outUrl: event.outUrl,
    }))
  }, [locale, topEventsList])

  // events主页 事件列表
  useEffect(() => {
    getEventsList({ offset: (pagination.currPage - 1) * pageLimit, limit: pageLimit }, eventsType).then((list) => {
      if (!!list.items) {
        // update pagination
        setPagination((v) => ({ ...v, totalPage: Math.ceil(list.count / pageLimit) }))

        const _enentsList: Array<EventsMeta> =
          list.items?.map((event) => ({
            docId: event.docId,
            title: event.title,
            mediaUrl: event.mediaUrl,
            inPlace: event.inPlace,
            place: event.place,
            jumpUrl: event?.docId ? `/events/${event?.docId}` : '#',
            date: `${formatDate(event.startTime, locale)} - ${formatDate(event.endTime, locale)}`,
            status: event.status,
            statusValue: event.statusValue,
            isOutUrl: event.isOutUrl,
            outUrl: event.outUrl,
          })) || []

        setEventsList(_enentsList)
      }
    })
  }, [pagination.currPage, eventsType, locale, pageLimit])

  const handlePageChange = (index: number) => {
    !!eventsWrapperRef.current && eventsWrapperRef.current.scrollIntoView({ behavior: 'smooth' })

    setPagination((prev) => ({
      ...prev,
      currPage: index,
    }))
  }

  const handleActiveTabChange = (index: number) => {
    !!eventsWrapperRef.current && eventsWrapperRef.current.scrollIntoView({ behavior: 'smooth' })

    setPagination((prev) => {
      setEventsType(eventstabItems[index].key)

      return {
        ...prev,
        currPage: 1,
      }
    })
  }

  const slideContent = useMemo<ReactNode>(() => {
    return (
      <div className={['w-full max-w-300 mx-auto'].join(' ')}>
        <div className="w-full grid gap-8 laptop:gap-4 grid-cols-1 laptop:grid-cols-2">
          {eventsList.map((event, index) => (
            <PdEventsCard key={index} data={event} />
          ))}
        </div>
        <Pagination
          className="mt-8 laptop:mt-12"
          currentPage={pagination.currPage}
          totalPages={pagination.totalPage}
          onPageChange={(page) => handlePageChange(page)}
        />
      </div>
    )
  }, [eventsList, pagination])

  return (
    <>
      <Head>
        <title>{seoData?.title || eventsTabsTitle} </title>
      </Head>
      <div className={['bg-white', 'laptop:bg-white', 'w-full'].join(' ')}>
        <div className="max-w-300 h-full mx-auto pt-16 laptop:py-16">
          <h2 className={['text-28 pl-6', 'laptop:text-4xl laptop:pl-0', 'font-semibold'].join(' ')}>{topTitle}</h2>

          <div
            className={[
              'relative box-content bg-pdbg mt-8',
              'laptop:mt-0 laptop:py-6 laptop:max-w-300 laptop:bg-white',
              'w-full laptop:h-508px',
            ].join(' ')}
          >
            {!!eventsBannerList?.length && (
              <PdSwiper
                effect="fade"
                fadeEffect={{ crossFade: true }}
                loop={true}
                autoplay={true}
                hideNavigation={isMobileDevice}
                paginationOptions={{
                  el: isMobileDevice
                    ? '.pd-pagination-container_white-tab-swiper'
                    : '.pd-pagination-container_black-tab-swiper',
                }}
              >
                {eventsBannerList.map((event, index) => (
                  <SwiperSlide key={index}>
                    <PdEventsBannerCard key={index} data={event} />
                  </SwiperSlide>
                ))}
              </PdSwiper>
            )}
            <div
              className={clsx(
                'absolute z-10 left-0',
                'h-fit top-224px',
                'laptop:top-auto laptop:!bottom-0',
                isMobileDevice ? 'pd-pagination-container_white-tab-swiper' : 'pd-pagination-container_black-tab-swiper'
              )}
            ></div>
          </div>
        </div>
      </div>

      <div
        id="eventsTabs"
        ref={eventsWrapperRef}
        className={clsx('mx-auto', 'w-full my-15', 'laptop:w-300 laptop:mt-16 laptop:mb-30')}
      >
        <SlidingTabs
          type="dynamic"
          containerClassName="w-full"
          childrenBoxClassName={clsx('px-6', 'laptop:px-0')}
          // contentBoxClassName={clsx('h-fit box-border')}
          tabsBoxClassName={clsx('bg-gray-100 sticky top-0 z-20', 'py-3 laptop:py-6 laptop:justify-start')}
          // tabsBoxClassName={clsx('pb-8 laptop:pb-6 laptop:justify-start')}
          tabsContainerClassName="laptop:rounded-full"
          // tabLabels={allEventsType.map((item) => item.label)}
          tabLabels={eventstabItems.map((item) => item.title)}
          tabEleClassName={clsx('!py-6px laptop:!py-2')}
          bgRefClassName={clsx('!h-8', 'laptop:!h-10')}
          activeTabChange={handleActiveTabChange}
        >
          {[slideContent].map((content, index) => (
            <div key={index}>{content}</div>
          ))}
        </SlidingTabs>
      </div>
    </>
  )
}

Events.getLayout = function (page: ReactElement, seo) {
  return (
    <PageLayout
      sticky={true}
      navbarMoveById="eventsTabs"
      pageTitle={seo.title}
      pageDescription={seo.description}
      pageKeywrods={seo.keywords}
      ogtype={'website'}
    >
      {page}
    </PageLayout>
  )
}

export default Events

export const getStaticProps = withSEO(
  async ({ locale }: GetStaticPropsContext) => {
    const messages = retriveMessages(['events'], locale as LocaleType)
    const topEventsList = await getTopEventsList(locale as string)
    return {
      props: {
        messages,
        initTopEventsList: topEventsList || {},
        seoData: (topEventsList as any)?.seoData,
      },
    }
  },
  () => '/events'
)
